import { useState, useEffect } from "react";
import { parseISO } from "date-fns";
import { useAxiosInstance } from "./useAxiosInstance";
import {
  DriverLogDetailedView,
  HosLogsApiFactory,
  ViolationType,
  ViolationViewDto,
} from "../data/service-proxies";
import { useVersion } from "react-admin";
import { getHosTimers } from "../../components/hos/hosUtils";

export const useDriverLog = (
  driverId: number | undefined,
  logDate: Date | undefined
) => {
  const [driverLog, setDriverLog] = useState<DriverLogDetailedView>();
  const version = useVersion();
  const [currentVersion, setCurrentVersion] = useState<number>(version);
  const axiosInstance = useAxiosInstance();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!driverId) {
      return;
    }

    if (!logDate) {
      return;
    }

    let driverLogDate: Date | undefined = undefined;
    if (driverLog?.date) {
      driverLogDate = parseISO(driverLog.date);
    }

    if (
      driverLog?.date &&
      driverLogDate &&
      driverLogDate.getFullYear() === logDate.getFullYear() &&
      driverLogDate.getMonth() === logDate.getMonth() &&
      driverLogDate.getDate() === logDate.getDate() &&
      driverLog?.driverId === driverId &&
      version === currentVersion
    ) {
      return;
    }

    if (!axiosInstance) {
      return;
    }

    setCurrentVersion(version);
    setLoading(true);
    HosLogsApiFactory(undefined, axiosInstance.defaults.baseURL, axiosInstance)
      .apiAppDriverlogsGetdriverdailylogPost({
        driverId,
        logDate: logDate.toISOString(),
      })
      .then((response) => {
        const { data } = response;
        if (!data.hosDetails) {
          setDriverLog(data);
          return;
        }

        const now = new Date();
        const hosTimers = getHosTimers(
          {
            ...data.hosDetails,
            cycleWorkTime: data.hosDetails?.cycleWorkTime,
          },
          now
        );

        const dayStart = parseISO(data.startOfDay!);
        if (hosTimers.breakViolationFrom) {
          const from =
            hosTimers.breakViolationFrom < dayStart
              ? dayStart
              : hosTimers.breakViolationFrom;
          const violation: ViolationViewDto = {
            isHosViolation: true,
            violationType: ViolationType.EightHoursViolation,
            startDateTime: from.toISOString(),
            startTime: from.getTime(),
            endDateTime: now.toISOString(),
            endTime: now.getTime(),
          };

          violation.violationError = violation.violationType;
          data.violations = [...(data.violations ?? []), violation];
        }

        if (hosTimers.shiftDriveViolationFrom) {
          const from =
            hosTimers.shiftDriveViolationFrom < dayStart
              ? dayStart
              : hosTimers.shiftDriveViolationFrom;
          const violation: ViolationViewDto = {
            isHosViolation: true,
            violationType: ViolationType.ElevenHoursViolation,
            startDateTime: from.toISOString(),
            startTime: from.getTime(),
            endDateTime: now.toISOString(),
            endTime: now.getTime(),
          };

          violation.violationError = violation.violationType;
          data.violations = [...(data.violations ?? []), violation];
        }

        if (hosTimers.shiftWorkViolationFrom) {
          const from =
            hosTimers.shiftWorkViolationFrom < dayStart
              ? dayStart
              : hosTimers.shiftWorkViolationFrom;
          const violation: ViolationViewDto = {
            isHosViolation: true,
            violationType: ViolationType.FourteenHoursViolation,
            startDateTime: from.toISOString(),
            startTime: from.getTime(),
            endDateTime: now.toISOString(),
            endTime: now.getTime(),
          };

          violation.violationError = violation.violationType;
          data.violations = [...(data.violations ?? []), violation];
        }

        if (hosTimers.cycleWorkViolationFrom) {
          const from =
            hosTimers.cycleWorkViolationFrom < dayStart
              ? dayStart
              : hosTimers.cycleWorkViolationFrom;
          const violation: ViolationViewDto = {
            isHosViolation: true,
            violationType: ViolationType.ThirtyFourHoursResetViolation,
            startDateTime: from.toISOString(),
            startTime: from.getTime(),
            endDateTime: now.toISOString(),
            endTime: now.getTime(),
          };

          violation.violationError = violation.violationType;
          data.violations = [...(data.violations ?? []), violation];
        }

        setDriverLog(data);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        setLoading(false);
      });
  }, [driverId, logDate, driverLog, axiosInstance, version, currentVersion]);

  return { driverLog, loading };
};
